import * as React from "react";
import { useWatch, Control, UseFieldArrayReturn } from "react-hook-form";
import { Grid, ActionIcon, Title, Stack, useMantineTheme, Flex } from "@mantine/core";
import {
  IconAnchor,
  IconChartLine,
  IconDatabase,
  IconDownload,
  IconEdit,
  IconFileUpload,
  IconLinkPlus,
  IconNotification,
  IconTrash,
} from "@tabler/icons-react";

import { CTextInput } from "@supportinitiative/react-hook-form-mantine";

import { UseCaseCVMContext } from "../UseCaseCVMContext";

import { Workflow } from "../Workflow/WorkflowCVM";

import {
  addEdgeViaPort,
  removeEdge,
  removeNode,
  updateAction,
  workflowInitialState,
  workflowReducer,
} from "../Workflow/WorkflowUtils";
import AddViaEdge from "../Workflow/Add/AddViaEdge";
import AddViaGroup from "../Workflow/Add/AddViaGroup";
import AddViaPort from "../Workflow/Add/AddViaPort";
import { UseCaseType } from "@supportinitiative/common";
import ActionRouter from "../Workflow/Action/ActionRouter";
import { useDebouncedValue } from "@mantine/hooks";
import DatasetsCVM from "../Dataset/DatasetsCVM";

const options = [
  { label: "Serial Group", name: "SERIAL_GROUP", icon: <IconLinkPlus /> },
  { label: "Parallel Group", name: "PARALLEL_GROUP", icon: <IconLinkPlus /> },
  { label: "Anchor Match", name: "ANCHOR_MATCH", icon: <IconAnchor /> },
  { label: "Bulk Dataload", name: "BULK_DATALOAD", icon: <IconFileUpload /> },
  { label: "Dataload", name: "DATALOAD", icon: <IconFileUpload /> },
  { label: "Derive Data", name: "DERIVE_DATA", icon: <IconDatabase /> },
  { label: "Notification", name: "NOTIFICATION", icon: <IconNotification /> },
  { label: "Report", name: "REPORT", icon: <IconDownload /> },
  { label: "Status Update", name: "STATUS_UPDATE", icon: <IconEdit /> },
  { label: "Summary Stats", name: "SUMMARY_STATS", icon: <IconChartLine /> },
];

const groupOptions = [
  { label: "Anchor Match", name: "ANCHOR_MATCH", icon: <IconAnchor /> },
  { label: "Bulk Dataload", name: "BULK_DATALOAD", icon: <IconFileUpload /> },
  { label: "Dataload", name: "DATALOAD", icon: <IconFileUpload /> },
  { label: "Derive Data", name: "DERIVE_DATA", icon: <IconDatabase /> },
  { label: "Notification", name: "NOTIFICATION", icon: <IconNotification /> },
  { label: "Report", name: "REPORT", icon: <IconDownload /> },
  { label: "Status Update", name: "STATUS_UPDATE", icon: <IconEdit /> },
  { label: "Summary Stats", name: "SUMMARY_STATS", icon: <IconChartLine /> },
];

function ReconciliationCVM({
  id,
  name,
  index,
  reconsArray,
  control,
  setActiveTab,
  fromWorkflowName,
}: {
  id: string;
  name: `definition.recons.${number}`;
  index: number;
  reconsArray: UseFieldArrayReturn<UseCaseType, "definition.recons", "key">;
  //reconsArray: any;
  control: Control<UseCaseType, any>;
  setActiveTab: (index: string) => void;
  fromWorkflowName: `definition.recons.${number}.definition` | "definition.orchestrator";
}) {
  const theme = useMantineTheme();

  const { state: useCaseFormState, dispatch: useCaseFormDispatch } = React.useContext(UseCaseCVMContext);

  const recon = useWatch({
    control,
    name: `definition.recons.${index}`,
  });

  const [debouncedReconName] = useDebouncedValue(recon.name, 300);

  const fromWorkflowNodes = useWatch({
    control,
    name: `${fromWorkflowName}.workflow.nodes`,
  });

  /// Workflow ////

  const [workflowState, workflowDispatch] = React.useReducer(workflowReducer, workflowInitialState);

  const workflow = useWatch({
    control,
    name: `${name}.definition.workflow`,
  });

  const cancel = () => {
    workflowDispatch({
      type: "RESET",
      value: {
        action: null,
        via: null,
      },
    });
  };

  const save = (values: any) => {
    const nodes = workflow && workflow.nodes ? workflow.nodes : [],
      edges = workflow && workflow.edges ? workflow.edges : [],
      actions = workflow && workflow.actions ? workflow.actions : [];
    const newWorkflow = updateAction(values, nodes, edges, actions);
    useCaseFormState.setValue(`${name}.definition.workflow`, newWorkflow);
    workflowDispatch({
      type: "RESET",
      value: {
        action: null,
        via: null,
      },
    });
  };

  /// Workflow ////

  React.useEffect(() => {
    const node = fromWorkflowNodes.find((n) => n.id === id);
    if (node && node.data && node.data.name !== recon.name) {
      const newNodes = fromWorkflowNodes.filter((n) => !(n.id === node.id));
      const newNode = {
        ...node,
        data: {
          ...node.data,
          name: recon.name ? recon.name : node.id,
          description: recon.description ? recon.description : node.id,
        },
      };
      newNodes.push(newNode);
      useCaseFormState.setValue(`${fromWorkflowName}.workflow.nodes`, newNodes);
    }
  }, [debouncedReconName]);

  return (
    <>
      <Stack
        style={{
          marginTop: theme.spacing.xs,
          padding: theme.spacing.xs,
          width: "100%",
        }}
      >
        <Stack gap="0">
          <Flex>
            <Title
              mb="xs"
              order={5}
              c="white"
              py={0}
              px={theme.spacing.xxs}
              style={{
                backgroundColor: theme.colors.emerald[0],
              }}
            >
              Name
            </Title>
          </Flex>
          <Grid align="stretch" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <CTextInput
                name={`${name}.name`}
                control={control}
                required
                disabled={useCaseFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            {!useCaseFormState.actionOptions.disableAll && (
              <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    reconsArray.remove(index);
                    setActiveTab(index === 0 ? "0" : (index - 1).toString());
                  }}
                  disabled={useCaseFormState.actionOptions.disableAll}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            )}
          </Grid>
        </Stack>

        <DatasetsCVM control={control} name={`definition.recons.${index}.definition`} />

        <Stack gap="0">
          <Flex>
            <Title
              mb="xs"
              order={5}
              c="white"
              py={0}
              px={theme.spacing.xxs}
              style={{
                backgroundColor: theme.colors.emerald[0],
              }}
            >
              Workflow
            </Title>
          </Flex>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            <Workflow
              allNodes={workflow && workflow.nodes ? workflow.nodes : []}
              allEdges={workflow && workflow.edges ? workflow.edges : []}
              disabled={useCaseFormState.actionOptions.disableAll}
              selectedEdge={workflowState.edge ? workflowState.edge : undefined}
              selectedNode={workflowState.node ? workflowState.node : undefined}
              onAddNode={(edge) => {
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "ADD",
                    via: "EDGE",
                    edge: edge,
                  },
                });
              }}
              onPortDragEnd={(fromNodeId, toNodeId) => {
                if (toNodeId) {
                  const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                    edges = workflow && workflow.edges ? workflow.edges : [],
                    actions = workflow && workflow.actions ? workflow.actions : [];

                  const newWorkflow = addEdgeViaPort({ from: fromNodeId, to: toNodeId }, nodes, edges, actions);
                  useCaseFormState.setValue(`${name}.definition.workflow`, newWorkflow);
                } else {
                  workflowDispatch({
                    type: "SET",
                    value: {
                      action: "ADD",
                      via: "PORT",
                      port: {
                        from: fromNodeId,
                        to: toNodeId,
                      },
                    },
                  });
                }
              }}
              onGroupAdd={(node) => {
                // console.log("onGroupAdd", node);
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "ADD",
                    via: "GROUP",
                    node: node,
                  },
                });
              }}
              onCanvasClick={() => {
                // console.log("onCanvasClick");
                // setSelectedEdge(undefined);
              }}
              onDeleteEdge={(edge) => {
                // console.log("onDeleteEdge");
                const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                  edges = workflow && workflow.edges ? workflow.edges : [],
                  actions = workflow && workflow.actions ? workflow.actions : [];

                const newWorkflow = removeEdge(edge, nodes, edges, actions);
                workflowDispatch({
                  type: "RESET",
                  value: {
                    action: null,
                    via: null,
                  },
                });
                // console.log("onDeleteEdge workflow", newWorkflow);
                useCaseFormState.setValue(`${name}.definition.workflow`, newWorkflow);
              }}
              onDeleteNode={(node) => {
                const nodes = workflow && workflow.nodes ? workflow.nodes : [],
                  edges = workflow && workflow.edges ? workflow.edges : [],
                  actions = workflow && workflow.actions ? workflow.actions : [];

                const newWorkflow = removeNode(node, nodes, edges, actions);
                workflowDispatch({
                  type: "RESET",
                  value: {
                    action: null,
                    via: null,
                  },
                });
                // console.log("onDeleteNode workflow", newWorkflow);
                useCaseFormState.setValue(`${name}.definition.workflow`, newWorkflow);
              }}
              onNodeClick={(node) => {
                // console.log("onNodeClick", node);
                if (
                  node.data?.type.toUpperCase() === "GROUP" ||
                  node.data?.type.toUpperCase() === "SERIAL_GROUP" ||
                  node.data?.type.toUpperCase() === "PARALLEL_GROUP"
                ) {
                  // do no ttrigger node click for group
                } else {
                  workflowDispatch({
                    type: "SET",
                    value: {
                      action: "VIEW",
                      via: "NODE",
                      node: node,
                    },
                  });
                }
              }}
              onEdgeClick={(edge) => {
                // console.log("onEdgeClick", edge);
                workflowDispatch({
                  type: "SET",
                  value: {
                    action: "VIEW",
                    via: "EDGE",
                    edge: edge,
                  },
                });
              }}
            />
          </div>
        </Stack>
      </Stack>

      {workflowState && workflowState.action === "ADD" && workflowState.via === "EDGE" && workflowState.edge && (
        <AddViaEdge
          name={`${name}.definition.workflow`}
          edge={workflowState.edge}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          options={options}
        />
      )}

      {workflowState && workflowState.action === "ADD" && workflowState.via === "PORT" && workflowState.port && (
        <AddViaPort
          name={`${name}.definition.workflow`}
          port={workflowState.port}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          reconsArray={reconsArray}
          options={options}
        />
      )}

      {workflowState && workflowState.action === "ADD" && workflowState.via === "GROUP" && workflowState.node && (
        <AddViaGroup
          name={`${name}.definition.workflow`}
          node={workflowState.node}
          workflowDispatch={workflowDispatch}
          nodes={workflow && workflow.nodes ? workflow.nodes : []}
          edges={workflow && workflow.edges ? workflow.edges : []}
          actions={workflow && workflow.actions ? workflow.actions : []}
          reconsArray={reconsArray}
          options={groupOptions}
        />
      )}

      {workflowState && workflowState.action === "VIEW" && workflowState.via === "NODE" && workflowState.node && (
        <ActionRouter
          node={workflowState.node}
          recon={recon}
          save={save}
          cancel={cancel}
          from={"RECONCILIATION"}
          actions={workflow && workflow.actions ? workflow.actions : []}
        />
      )}
    </>
  );
}

export default ReconciliationCVM;
